import React, { useMemo, useState } from "react";
import {
  Box,
  Button,
  Card,
  CircularProgress,
  Grid,
  TextField,
  Typography,
  Autocomplete,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
} from "@mui/material";
import { useFormik } from "formik";
import SaveIcon from "@mui/icons-material/Save";
import CloseIcon from "@mui/icons-material/Close";
import {
  useCreatePaymentMutation,
  useGetInvoiceQuery,
} from "../../../Api/attoDeskApi";
import { useNotifier } from "../../../Core/Notifier";
import { useStyles } from "../../../theme/appStyles";
import { useNavigate } from "react-router-dom";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs, { Dayjs } from "dayjs";

const Index = () => {
  const classes = useStyles();
  const navigate = useNavigate();
  const { showErrorMessage, showMessage } = useNotifier();

  const [createPayment, { isLoading }] = useCreatePaymentMutation();

  const { data: invoiceData } = useGetInvoiceQuery({ status: "sent" });
  const invoiceList = useMemo(() => Array.isArray(invoiceData?.data) ? (invoiceData?.data as any[]) : [], [invoiceData]);

  const formik = useFormik({
    initialValues: {
      invoiceId: "",
      invoiceDisplay: "",
      amount: "",
      paymentDate: dayjs() as Dayjs | null,
      paymentType: "full",
      paymentMethod: "cash",
      notes: "",
    },
    onSubmit: async (values, { resetForm }) => {
      try {
        const payload = {
          invoiceId: values.invoiceId,
          amount: parseFloat(values.amount) || 0,
          paymentDate: values.paymentDate ? values.paymentDate.format("YYYY-MM-DD") : "",
          paymentType: values.paymentType,
          paymentMethod: values.paymentMethod,
          notes: values.notes,
        };

        const response = await createPayment(payload).unwrap();
        if (response.status) {
          showMessage(response.message);
          resetForm();
          navigate("/payment/list");
        } else {
          showErrorMessage(response.message);
        }
      } catch (error) {
        showErrorMessage("Something went wrong");
      }
    },
  });

  const formValid = useMemo(() => {
    return (
      formik.values.invoiceId !== "" &&
      formik.values.amount !== "" &&
      parseFloat(formik.values.amount) > 0
    );
  }, [formik.values]);

  const handleInvoiceChange = (invoice: any) => {
    if (invoice) {
      formik.setFieldValue("invoiceId", invoice.id?.toString() || "");
      formik.setFieldValue("invoiceDisplay", `#${invoice.invoiceNumber || invoice.id} - ${invoice.studentName || ""} - $${parseFloat(invoice.totalAmount || invoice.total || 0).toFixed(2)}`);
      // Auto-fill amount for full payment
      if (formik.values.paymentType === "full") {
        formik.setFieldValue("amount", (invoice.totalAmount || invoice.total || 0).toString());
      }
    } else {
      formik.setFieldValue("invoiceId", "");
      formik.setFieldValue("invoiceDisplay", "");
    }
  };

  return (
    <div>
      <Grid container spacing={3}>
        <Grid item lg={3} md={3} sm={12} xs={12}>
          <Card className={classes.stepperCard}>
            <Grid
              container
              justifyContent={"space-between"}
              spacing={5}
              sx={{ borderBottom: 2, padding: 2 }}
              className={classes.titleGrid}
            >
              <Grid item lg={12} md={12} sm={12} xs={12} pt={2} alignContent={"center"}>
                <Typography className={classes.cardTitle}>Record Payment</Typography>
              </Grid>
            </Grid>
          </Card>
        </Grid>
        <Grid item lg={9} md={9} sm={12} xs={12}>
          <form onSubmit={formik.handleSubmit}>
            <Grid container>
              <Grid item lg={12} md={12} sm={12} xs={12}>
                <Card className={classes.card}>
                  <Grid
                    container
                    justifyContent={"space-between"}
                    spacing={5}
                    sx={{ borderBottom: 2, padding: 2 }}
                    className={classes.titleGrid}
                  >
                    <Grid item lg={12} md={12} sm={12} xs={12} pt={2} alignContent={"center"}>
                      <Typography className={classes.cardTitle}>Payment Information</Typography>
                    </Grid>
                  </Grid>

                  <Grid container spacing={3} p={"24px"}>
                    <Grid item lg={12} md={12} sm={12} xs={12}>
                      <Grid container alignItems="center">
                        <Grid item lg={12} md={12} sm={12} xs={12}>
                          <Typography variant="subtitle1">
                            Invoice <span className={classes.requiedfield}>*</span>
                          </Typography>
                        </Grid>
                        <Grid item lg={12} md={12} sm={12} xs={12}>
                          <Autocomplete
                            options={invoiceList}
                            getOptionLabel={(option: any) =>
                              `#${option.invoiceNumber || option.id} - ${option.studentName || `${option.studentFirstName || ""} ${option.studentLastName || ""}`} - $${parseFloat(option.totalAmount || option.total || 0).toFixed(2)}`
                            }
                            onChange={(e, val) => handleInvoiceChange(val)}
                            renderInput={(params) => (
                              <TextField {...params} placeholder="Search invoice..." size="small" className={classes.textField} />
                            )}
                          />
                        </Grid>
                      </Grid>
                    </Grid>

                    <Grid item lg={6} md={6} sm={12} xs={12}>
                      <Grid container alignItems="center">
                        <Grid item lg={12} md={12} sm={12} xs={12}>
                          <Typography variant="subtitle1">
                            Amount <span className={classes.requiedfield}>*</span>
                          </Typography>
                        </Grid>
                        <Grid item lg={12} md={12} sm={12} xs={12}>
                          <TextField
                            placeholder="0.00"
                            size="small"
                            type="number"
                            {...formik.getFieldProps("amount")}
                            className={classes.textField}
                            InputProps={{ sx: { fontSize: 14 } }}
                            inputProps={{ min: 0, step: 0.01 }}
                          />
                        </Grid>
                      </Grid>
                    </Grid>

                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                      <Grid item lg={6} md={6} sm={12} xs={12}>
                        <Grid container alignItems="center">
                          <Grid item lg={12} md={12} sm={12} xs={12}>
                            <Typography variant="subtitle1">Payment Date</Typography>
                          </Grid>
                          <Grid item lg={12} md={12} sm={12} xs={12}>
                            <DatePicker
                              value={formik.values.paymentDate}
                              onChange={(val) => formik.setFieldValue("paymentDate", val)}
                              slotProps={{ textField: { size: "small", fullWidth: true, className: classes.textField } }}
                            />
                          </Grid>
                        </Grid>
                      </Grid>
                    </LocalizationProvider>

                    <Grid item lg={6} md={6} sm={6} xs={12}>
                      <Grid container alignItems="center">
                        <Grid item lg={12} md={12} sm={12} xs={12}>
                          <Typography variant="subtitle1">Payment Type</Typography>
                        </Grid>
                        <Grid item lg={12} md={12} sm={12} xs={12}>
                          <FormControl fullWidth size="small">
                            <Select
                              value={formik.values.paymentType}
                              onChange={(e) => formik.setFieldValue("paymentType", e.target.value)}
                              className={classes.textField}
                            >
                              <MenuItem value="full">Full Payment</MenuItem>
                              <MenuItem value="partial">Partial Payment</MenuItem>
                              <MenuItem value="refund">Refund</MenuItem>
                            </Select>
                          </FormControl>
                        </Grid>
                      </Grid>
                    </Grid>

                    <Grid item lg={6} md={6} sm={6} xs={12}>
                      <Grid container alignItems="center">
                        <Grid item lg={12} md={12} sm={12} xs={12}>
                          <Typography variant="subtitle1">Payment Method</Typography>
                        </Grid>
                        <Grid item lg={12} md={12} sm={12} xs={12}>
                          <FormControl fullWidth size="small">
                            <Select
                              value={formik.values.paymentMethod}
                              onChange={(e) => formik.setFieldValue("paymentMethod", e.target.value)}
                              className={classes.textField}
                            >
                              <MenuItem value="cash">Cash</MenuItem>
                              <MenuItem value="card">Card</MenuItem>
                              <MenuItem value="bank_transfer">Bank Transfer</MenuItem>
                              <MenuItem value="cheque">Cheque</MenuItem>
                              <MenuItem value="online">Online</MenuItem>
                            </Select>
                          </FormControl>
                        </Grid>
                      </Grid>
                    </Grid>

                    <Grid item lg={12} md={12} sm={12} xs={12}>
                      <Grid container alignItems="center">
                        <Grid item lg={12} md={12} sm={12} xs={12}>
                          <Typography variant="subtitle1">Notes</Typography>
                        </Grid>
                        <Grid item lg={12} md={12} sm={12} xs={12}>
                          <TextField
                            placeholder="Payment notes..."
                            size="small"
                            multiline
                            rows={4}
                            {...formik.getFieldProps("notes")}
                            className={classes.discriptionTextField}
                            InputProps={{ sx: { fontSize: 14 } }}
                          />
                        </Grid>
                      </Grid>
                    </Grid>
                  </Grid>
                </Card>
              </Grid>

              <Grid item lg={12} md={12} sm={12} xs={12} pt={3}>
                <Card className={classes.card}>
                  <Grid container spacing={3} p={"24px"}>
                    <Grid
                      item
                      lg={12}
                      md={12}
                      sm={12}
                      xs={12}
                      sx={{ display: "flex", justifyContent: "flex-end" }}
                    >
                      <Button
                        variant="outlined"
                        startIcon={<CloseIcon />}
                        className={classes.addButton}
                        onClick={() => navigate("/payment/list")}
                      >
                        Cancel
                      </Button>
                      <Box m={0.5}></Box>
                      <Button
                        variant="contained"
                        startIcon={isLoading ? <CircularProgress size={18} className={classes.isLoading} /> : <SaveIcon />}
                        className={classes.addButton}
                        onClick={() => formik.handleSubmit()}
                        disabled={!formValid || isLoading}
                      >
                        Record Payment
                      </Button>
                    </Grid>
                  </Grid>
                </Card>
              </Grid>
            </Grid>
          </form>
        </Grid>
      </Grid>
    </div>
  );
};

export default Index;
